import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * ShopHistory component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconShopHistory extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.28418 1.88525C1.28418 1.47104 1.61997 1.13525 2.03418 1.13525H4.51566C4.86842 1.13522 5.2105 1.25704 5.48376 1.48011C5.75078 1.69809 5.93624 1.99969 6.01042 2.33588L6.51467 4.25684H11.0086C11.4228 4.25684 11.7586 4.59262 11.7586 5.00684C11.7586 5.42105 11.4228 5.75684 11.0086 5.75684H6.85089L8.11167 12.0607H20.274C20.2775 12.0608 20.281 12.0596 20.2836 12.0574C20.2865 12.055 20.2882 12.0518 20.2888 12.0486C20.3562 11.6399 20.7423 11.3633 21.1509 11.4308C21.5596 11.4983 21.8362 11.8843 21.7687 12.293C21.7096 12.6508 21.5241 12.9753 21.2463 13.2077C20.9698 13.4391 20.62 13.5642 20.2597 13.5607H8.41183L8.88745 15.9388C8.88892 15.9455 8.89259 15.9514 8.8979 15.9558C8.90324 15.9601 8.91007 15.9626 8.91728 15.9626H18.4224C18.8366 15.9626 19.1724 16.2984 19.1724 16.7126C19.1724 17.1269 18.8366 17.4626 18.4224 17.4626H8.91728C8.5647 17.4626 8.22272 17.341 7.94934 17.1178C7.67606 16.8947 7.4882 16.584 7.41765 16.2383L7.41706 16.2354L6.76454 12.9728L6.76138 12.9578L5.20468 5.17429L4.55518 2.69999C4.55166 2.6866 4.54852 2.67311 4.54575 2.65955C4.54435 2.65271 4.54063 2.64654 4.53519 2.6421C4.52976 2.63767 4.52298 2.63525 4.51597 2.63525H2.03418C1.61997 2.63525 1.28418 2.29947 1.28418 1.88525ZM18.0022 2.63525C16.2269 2.63525 14.7878 4.07436 14.7878 5.84959C14.7878 7.62482 16.2269 9.06392 18.0022 9.06392C19.7774 9.06392 21.2165 7.62482 21.2165 5.84959C21.2165 4.07436 19.7774 2.63525 18.0022 2.63525ZM13.2878 5.84959C13.2878 3.24593 15.3985 1.13525 18.0022 1.13525C20.6058 1.13525 22.7165 3.24593 22.7165 5.84959C22.7165 8.45324 20.6058 10.5639 18.0022 10.5639C15.3985 10.5639 13.2878 8.45324 13.2878 5.84959ZM19.5236 5.38897C19.8165 5.09608 19.8165 4.62121 19.5236 4.32831C19.2307 4.03542 18.7558 4.03542 18.463 4.32831L17.4719 5.3194C17.179 5.61229 17.179 6.08716 17.4719 6.38006C17.7648 6.67295 18.2396 6.67295 18.5325 6.38006L19.5236 5.38897ZM11.0002 21.2788C11.0002 22.1072 10.3287 22.7788 9.50024 22.7788C8.67182 22.7788 8.00024 22.1072 8.00024 21.2788C8.00024 20.4504 8.67182 19.7788 9.50024 19.7788C10.3287 19.7788 11.0002 20.4504 11.0002 21.2788ZM18.0002 22.7788C18.8287 22.7788 19.5002 22.1072 19.5002 21.2788C19.5002 20.4504 18.8287 19.7788 18.0002 19.7788C17.1718 19.7788 16.5002 20.4504 16.5002 21.2788C16.5002 22.1072 17.1718 22.7788 18.0002 22.7788Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-shop-history') === undefined) {
  customElements.define('cat-icon-shop-history', BlocksIconShopHistory);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-shop-history': BlocksIconShopHistory;
  }
}
